import { createSignal, onCleanup } from 'solid-js'

/**
 * Dictado por voz con la API de reconocimiento del navegador.
 *
 * No hay servicio propio detrás: `SpeechRecognition` (con prefijo `webkit` en
 * Chrome y Edge) transcribe en el equipo del usuario. Firefox no la trae, así
 * que `supported` sale falso y el botón ni se pinta — mejor eso que un botón
 * que no hace nada.
 *
 * **El dictado largo se sostiene reanudando el reconocimiento.** Aunque se pida
 * `continuous`, el navegador termina la sesión solo: al primer silencio de unos
 * segundos, al cabo de aproximadamente un minuto de audio, o cuando el servicio
 * corta. Con la implementación anterior eso apagaba el micrófono a media frase y
 * había que volver a pulsar el botón cada dos por tres. Ahora se guarda la
 * intención del usuario (`wanted`) y, mientras siga queriendo dictar, cada
 * `end` arranca una sesión nueva.
 *
 * Los reinicios se cuentan: si el navegador cierra la sesión una y otra vez sin
 * llegar a escuchar nada (micrófono ocupado, sin permiso real, sin red), se para
 * de verdad en vez de quedarse en un bucle invisible.
 */
type SpeechRecognitionLike = {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives?: number
  start: () => void
  stop: () => void
  abort: () => void
  onresult: ((event: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null
  onerror: ((event: { error: string }) => void) | null
  onend: (() => void) | null
  onstart?: (() => void) | null
  onspeechstart?: (() => void) | null
}

type SpeechWindow = typeof window & {
  SpeechRecognition?: new () => SpeechRecognitionLike
  webkitSpeechRecognition?: new () => SpeechRecognitionLike
}

export interface DictationOptions {
  /** Idioma del reconocimiento. Por defecto, el del documento o español. */
  lang?: string
  /** Se llama con cada fragmento ya confirmado. */
  onText: (text: string) => void
  onError?: (message: string) => void
}

/** Reinicios seguidos sin haber oído nada antes de darse por vencido. */
const MAX_REINTENTOS_EN_VACIO = 4

export function useDictation(options: DictationOptions) {
  const getConstructor = () => {
    const w = window as SpeechWindow
    return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
  }

  const supported = typeof window !== 'undefined' && Boolean(getConstructor())
  const [listening, setListening] = createSignal(false)
  const [interim, setInterim] = createSignal('')

  let recognition: SpeechRecognitionLike | null = null
  /** Lo que quiere el usuario, que no es lo mismo que el estado del navegador. */
  let wanted = false
  let reintentosEnVacio = 0
  let restartTimer: ReturnType<typeof setTimeout> | undefined

  const clearTimer = () => {
    if (restartTimer) clearTimeout(restartTimer)
    restartTimer = undefined
  }

  /** Suelta la sesión actual sin tocar la intención del usuario. */
  const releaseSession = () => {
    if (!recognition) return
    recognition.onresult = null
    recognition.onerror = null
    recognition.onend = null
    try {
      recognition.abort()
    } catch {
      // Ya estaba cerrada.
    }
    recognition = null
  }

  const stop = () => {
    wanted = false
    reintentosEnVacio = 0
    clearTimer()
    releaseSession()
    setListening(false)
    setInterim('')
  }

  const startSession = () => {
    const Ctor = getConstructor()
    if (!Ctor) return

    try {
      recognition = new Ctor()
    } catch {
      options.onError?.('No se pudo iniciar el dictado')
      stop()
      return
    }

    recognition.lang = options.lang ?? document.documentElement.lang ?? 'es-ES'
    recognition.continuous = true
    recognition.interimResults = true
    recognition.maxAlternatives = 1

    recognition.onspeechstart = () => {
      // Se oyó algo: la racha de reinicios en vacío se acabó.
      reintentosEnVacio = 0
    }

    recognition.onresult = event => {
      reintentosEnVacio = 0
      let pending = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const transcript = result[0]?.transcript ?? ''
        if (result.isFinal) options.onText(transcript)
        else pending += transcript
      }
      setInterim(pending)
    }

    recognition.onerror = event => {
      const error = event.error

      // Sin permiso no hay nada que reintentar.
      if (error === 'not-allowed' || error === 'service-not-allowed') {
        options.onError?.('Permiso de micrófono denegado')
        stop()
        return
      }
      // Silencio o corte del servicio: `end` llegará justo después y allí se
      // reanuda. Avisar de esto en pantalla sólo sería ruido.
      if (error === 'no-speech' || error === 'aborted' || error === 'network' || error === 'audio-capture') return

      options.onError?.(`Dictado: ${error}`)
    }

    recognition.onend = () => {
      setInterim('')
      if (!wanted) {
        setListening(false)
        return
      }

      reintentosEnVacio += 1
      if (reintentosEnVacio > MAX_REINTENTOS_EN_VACIO) {
        options.onError?.('El dictado se detuvo: revisa el micrófono')
        stop()
        return
      }

      // Un respiro antes de reanudar: encadenar `start()` dentro de `end` lanza
      // "recognition has already started" en Chrome.
      clearTimer()
      restartTimer = setTimeout(() => {
        if (!wanted) return
        releaseSession()
        startSession()
      }, 250)
    }

    try {
      recognition.start()
      setListening(true)
    } catch {
      // Suele ser una sesión anterior que aún no cerró: se reintenta una vez.
      clearTimer()
      restartTimer = setTimeout(() => {
        if (wanted) startSession()
      }, 400)
    }
  }

  const start = () => {
    if (wanted) return
    wanted = true
    reintentosEnVacio = 0
    startSession()
  }

  const toggle = () => (wanted ? stop() : start())

  onCleanup(() => {
    wanted = false
    clearTimer()
    releaseSession()
  })

  return { supported, listening, interim, start, stop, toggle }
}
